/**
 * The worklist view of one audit run (audit remodel): triage tiles, run-wide
 * search, and severity-ordered collapsible groups — the daily work queue, with
 * the green expected categories standing collapsed at the bottom as reference
 * groups. Rows saved before the remodel are surfaced as an unlabeled notice,
 * never hidden. Visual spec: the approved audit-remodel mockup artifact.
 *
 * Decluttered 2026-09-28 (Ian; mockup
 * https://claude.ai/artifact/Jn35xvQ4YA5PU5WZEQcua2): the problem chips show
 * only under an opened severity tile instead of as an always-on row; the
 * vendor-status legend moved into the page's Help pop-up; and each severity is
 * named once, in a heading above its groups, instead of on every group row.
 */
import React, { useEffect, useRef, useState } from "react"

import {
  getProblemSummary,
  getRunDiff,
  listWorkedMarks,
  ProblemSummaryEntry,
  ProblemVehiclesResponse,
  putWorkedMark,
  removeWorkedMark,
  RunDiffResponse,
  searchRunVehicles,
  VehicleResponse,
  WorkedMarkEntry,
} from "../../../api/audit"
import { THEME } from "../../../config/theme"
import { useDealerGroup } from "../../../context/DealerGroupContext"
import { useProblemTypes } from "../../../hooks/useProblemTypes"
import ErrorBoundary from "../../ErrorBoundary"
import SearchInput from "../SearchInput"
import GroupVehicleTable from "./GroupVehicleTable"
import { orderBySeverityThenCount } from "./ProblemChipsRow"
import ResolvedDigest from "./ResolvedDigest"
import RowDrawer from "./RowDrawer"
import SeverityChipsPanel from "./SeverityChipsPanel"
import SeverityDot, { severityWord } from "./SeverityDot"
import TriageTiles, { TileSeverity } from "./TriageTiles"
import { NotOnWebsiteSummary, getNotOnWebsiteSummary } from "../../../api/notOnWebsite"
import WorklistGroup from "./WorklistGroup"

interface Props {
  runId: number
  /** Page-level store view (rooftop slug) — null shows the whole group. The
   *  backend narrows counts, rows, search, and the diff to the same store,
   *  so the tiles can never disagree with the lists beneath them. */
  rooftopSlug: string | null
}

/** The work-queue severities, in the order their sections stand on the page. */
const PROBLEM_SEVERITY_ORDER = ["critical", "warning", "cleanup"]
const GROUP_SEVERITIES = new Set(PROBLEM_SEVERITY_ORDER)
/** The bucket a slug the registry does not know falls into. Cleanup sections
 * render like the others — Ian (2026-10-08) rejected folding them to one line. */
const CLEANUP_SEVERITY = "cleanup"
/** Only this bucket's groups carry NEW pills; a critical group shows days open. */
const WARNING_SEVERITY = "warning"

/**
 * The one place a severity is named in the list: a small heading above its
 * run of groups, so the rows beneath can carry just a dot. Section-eyebrow
 * styling after FileRequirementsPanel's "REQUIRED"/"OPTIONAL" labels.
 */
function SeverityHeading({ severity, groups }: { severity: string; groups: ProblemSummaryEntry[] }) {
  const vehicleCount = groups.reduce((sum, entry) => sum + entry.count, 0)
  const noun = severity === "expected" ? "group" : "problem"
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: THEME.spacing.sm,
        margin: `${THEME.spacing.md} 0 ${THEME.spacing.sm}`,
        fontSize: THEME.typography.caption.size,
        fontWeight: THEME.typography.caption.weight,
        letterSpacing: THEME.typography.caption.tracking,
        textTransform: "uppercase",
        color: THEME.colors.neutral.light.textSecondary,
      }}
    >
      <SeverityDot severity={severity} decorative />
      {severityWord(severity)}
      <span style={{ textTransform: "none", letterSpacing: 0, fontWeight: 400 }}>
        · {groups.length} {groups.length === 1 ? noun : `${noun}s`} ·{" "}
        {vehicleCount.toLocaleString()} {vehicleCount === 1 ? "vehicle" : "vehicles"}
      </span>
    </div>
  )
}

export default function AuditWorklistView({ runId, rooftopSlug }: Props) {
  const registry = useProblemTypes()
  const { dealerGroupId, websiteDomain } = useDealerGroup()
  const [entries, setEntries] = useState<ProblemSummaryEntry[] | null>(null)
  const [unlabeledCount, setUnlabeledCount] = useState(0)
  const [diff, setDiff] = useState<RunDiffResponse | null>(null)
  const [marks, setMarks] = useState<WorkedMarkEntry[]>([])
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [expandedSlugs, setExpandedSlugs] = useState<Set<string>>(new Set())
  // The triage tile whose problem chips are showing; all start closed.
  const [openSeverity, setOpenSeverity] = useState<TileSeverity | null>(null)
  const groupRefs = useRef<Record<string, HTMLDivElement | null>>({})

  // The "Not on the website" tile's figures: the group's newest saved point,
  // one row on the server, independent of which run is selected. A failure
  // leaves the tile as a bare link — it is a door, not a readout.
  const [notOnWebsite, setNotOnWebsite] = useState<NotOnWebsiteSummary | null>(null)
  useEffect(() => {
    if (dealerGroupId === null) return undefined
    let cancelled = false
    // The same store view as every other tile in the band: in the Dodge view
    // the red tile must say Dodge's figures, not the whole group's.
    getNotOnWebsiteSummary(dealerGroupId, rooftopSlug)
      .then((summary) => {
        if (!cancelled) setNotOnWebsite(summary)
      })
      .catch(() => {
        if (!cancelled) setNotOnWebsite(null)
      })
    return () => {
      cancelled = true
    }
  }, [dealerGroupId, rooftopSlug])

  // In-group search: two or more characters searches the whole run (VIN,
  // stock, make, model) and swaps the group list for a flat result table.
  const [searchQuery, setSearchQuery] = useState("")
  const [searchResults, setSearchResults] = useState<ProblemVehiclesResponse | null>(null)
  useEffect(() => {
    const q = searchQuery.trim()
    if (q.length < 2) {
      setSearchResults(null)
      return
    }
    let cancelled = false
    searchRunVehicles(runId, q, 50, rooftopSlug)
      .then((results) => {
        if (!cancelled) setSearchResults(results)
      })
      .catch(() => {
        if (!cancelled) setSearchResults(null)
      })
    return () => {
      cancelled = true
    }
  }, [searchQuery, runId, rooftopSlug])

  // Load the run's summary, run-over-run diff, and worked marks together
  // whenever the selected run — or the store view — changes. Diff/marks
  // failures degrade quietly — the worklist still renders without tags.
  useEffect(() => {
    let cancelled = false
    setEntries(null)
    setError(null)
    setDiff(null)
    setMarks([])
    setSelectedVehicle(null)
    setExpandedSlugs(new Set())
    setOpenSeverity(null)
    ;(async () => {
      try {
        const summary = await getProblemSummary(runId, rooftopSlug)
        if (cancelled) return
        setEntries(summary.problems)
        setUnlabeledCount(summary.unlabeled_count)
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to load the worklist")
        }
        return
      }
      try {
        const [diffResponse, marksResponse] = await Promise.all([
          getRunDiff(runId, rooftopSlug),
          listWorkedMarks(runId),
        ])
        if (!cancelled) {
          setDiff(diffResponse)
          setMarks(marksResponse.marks)
        }
      } catch {
        // Tags are enrichment — the worklist works without them.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [runId, rooftopSlug])

  // Every group starts collapsed — the operator chooses what to open (Ian's
  // ruling 2026-08-12, superseding the mockup's auto-expanded first group).

  if (error !== null) {
    return (
      <div style={{ padding: THEME.spacing.lg, fontSize: 13, color: THEME.colors.semantic.danger }}>
        {error}
      </div>
    )
  }
  if (entries === null) {
    return (
      <div style={{ padding: THEME.spacing.lg, fontSize: 13, color: THEME.colors.neutral.light.textSecondary }}>
        Loading worklist…
      </div>
    )
  }

  const severityOf = (slug: string): string => registry?.[slug]?.severity ?? CLEANUP_SEVERITY
  // Which triage tile a group counts toward: its own severity for the work
  // queue, and the green Expected tile for everything standing below it —
  // the same split the group list makes.
  const tileSeverityOf = (slug: string): TileSeverity => {
    const severity = severityOf(slug)
    return GROUP_SEVERITIES.has(severity) ? (severity as TileSeverity) : "expected"
  }
  const tileCounts: Record<TileSeverity, number> = { critical: 0, warning: 0, cleanup: 0, expected: 0 }
  for (const entry of entries) tileCounts[tileSeverityOf(entry.problem_type)] += entry.count

  // Problem groups (critical/warning/info) are the work queue; the green
  // expected categories stand below them as always-present reference groups,
  // collapsed by default (Ian's ruling 2026-08-11: greens are static on the
  // page, not click-to-summon).
  const ordered = orderBySeverityThenCount(entries, registry)
  const problemGroups = ordered.filter((entry) => GROUP_SEVERITIES.has(severityOf(entry.problem_type)))
  const greenGroups = ordered.filter((entry) => !GROUP_SEVERITIES.has(severityOf(entry.problem_type)))
  const problemSections = PROBLEM_SEVERITY_ORDER
    .map((severity) => ({
      severity,
      groups: problemGroups.filter((entry) => severityOf(entry.problem_type) === severity),
    }))
    .filter((section) => section.groups.length > 0)

  const toggleSeverity = (severity: TileSeverity) =>
    setOpenSeverity((open) => (open === severity ? null : severity))

  const toggleGroup = (slug: string) => {
    setExpandedSlugs((open) => {
      const next = new Set(open)
      if (next.has(slug)) {
        next.delete(slug)
      } else {
        next.add(slug)
      }
      return next
    })
  }

  // Every chip — green included — scrolls to and expands its standing group.
  const handleChipClick = (slug: string) => {
    setExpandedSlugs((open) => new Set(open).add(slug))
    groupRefs.current[slug]?.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  const marksBySlug = (slug: string): Map<string, string> => {
    const map = new Map<string, string>()
    for (const mark of marks) {
      if (mark.problem_type === slug) map.set(mark.vin, mark.marked_at)
    }
    return map
  }

  const toggleWorked = async (vehicle: VehicleResponse, nextWorked: boolean) => {
    if (dealerGroupId === null || !vehicle.vin || !vehicle.problem_type) return
    const vin = vehicle.vin
    const slug = vehicle.problem_type
    try {
      if (nextWorked) {
        await putWorkedMark(dealerGroupId, vin, slug)
        setMarks((current) => [
          ...current.filter((m) => !(m.vin === vin && m.problem_type === slug)),
          { vin, problem_type: slug, marked_at: new Date().toISOString() },
        ])
      } else {
        await removeWorkedMark(dealerGroupId, vin, slug)
        setMarks((current) =>
          current.filter((m) => !(m.vin === vin && m.problem_type === slug))
        )
      }
    } catch {
      // Leave state unchanged; the checkbox simply doesn't stick.
    }
  }

  return (
    <div>
      <TriageTiles
        counts={tileCounts}
        diff={diff}
        notOnWebsite={notOnWebsite}
        siteDomain={websiteDomain}
        openSeverity={openSeverity}
        onSeverityClick={toggleSeverity}
      />
      {openSeverity !== null && (
        <SeverityChipsPanel
          severity={openSeverity}
          entries={ordered.filter((entry) => tileSeverityOf(entry.problem_type) === openSeverity)}
          registry={registry}
          onChipClick={handleChipClick}
          onClose={() => setOpenSeverity(null)}
        />
      )}
      {/* Search owns the list below it — full width directly above the
          groups, mirroring the sheet view's bar-above-the-table pattern. */}
      <div style={{ marginBottom: 14 }}>
        <SearchInput
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Search this run — VIN, stock, make, model…"
        />
      </div>
      {unlabeledCount > 0 && (
        <div
          style={{
            border: `1px dashed ${THEME.colors.neutral.light.border}`,
            borderRadius: THEME.borderRadius.md,
            padding: "10px 14px",
            marginBottom: 14,
            fontSize: 12.5,
            color: THEME.colors.neutral.light.textSecondary,
          }}
        >
          {unlabeledCount.toLocaleString()} vehicles in this run were saved before problem
          labels existed — switch to Sheet view to see them.
        </div>
      )}
      {searchResults !== null ? (
        <div
          style={{
            border: `1px solid ${THEME.colors.neutral.light.border}`,
            borderRadius: THEME.borderRadius.md,
            overflow: "hidden",
            background: THEME.colors.neutral.light.surface,
          }}
        >
          <div
            style={{
              padding: "10px 16px",
              fontSize: 12.5,
              color: THEME.colors.neutral.light.textSecondary,
              borderBottom: `1px solid ${THEME.colors.neutral.light.border}`,
            }}
          >
            {searchResults.total.toLocaleString()}{" "}
            {searchResults.total === 1 ? "match" : "matches"} for "{searchQuery.trim()}"
            {searchResults.total > searchResults.vehicles.length &&
              ` — showing the first ${searchResults.vehicles.length}`}
          </div>
          {searchResults.vehicles.length > 0 && (
            <GroupVehicleTable
              vehicles={searchResults.vehicles}
              onRowClick={setSelectedVehicle}
              problemLabelFor={(vehicle) =>
                vehicle.problem_type
                  ? registry?.[vehicle.problem_type]?.label ?? vehicle.problem_type
                  : null
              }
            />
          )}
        </div>
      ) : problemGroups.length === 0 ? (
        <div style={{ padding: THEME.spacing.lg, fontSize: 13, color: THEME.colors.semantic.success }}>
          No open problems in this run.
        </div>
      ) : (
        problemSections.map(({ severity, groups }) => (
          <React.Fragment key={severity}>
            <SeverityHeading severity={severity} groups={groups} />
            {groups.map((entry) => (
              <div
                key={entry.problem_type}
                ref={(node) => {
                  groupRefs.current[entry.problem_type] = node
                }}
              >
                <WorklistGroup
                  runId={runId}
                  rooftopSlug={rooftopSlug}
                  slug={entry.problem_type}
                  count={entry.count}
                  info={registry?.[entry.problem_type] ?? null}
                  expanded={expandedSlugs.has(entry.problem_type)}
                  onToggle={() => toggleGroup(entry.problem_type)}
                  // NEW belongs to warnings only: a critical is past its
                  // grace by definition and shows days open instead, so
                  // "just appeared" can never read as "nothing to fix".
                  newVins={
                    severity === WARNING_SEVERITY
                      ? new Set(diff?.new_vins_by_problem[entry.problem_type] ?? [])
                      : undefined
                  }
                  oldestFirstSeenAt={entry.oldest_first_seen_at}
                  workedMarks={marksBySlug(entry.problem_type)}
                  onRowClick={setSelectedVehicle}
                />
              </div>
            ))}
          </React.Fragment>
        ))
      )}
      {searchResults === null && greenGroups.length > 0 && (
        <SeverityHeading severity="expected" groups={greenGroups} />
      )}
      {searchResults === null &&
        greenGroups.map((entry) => (
          <div
            key={entry.problem_type}
            ref={(node) => {
              groupRefs.current[entry.problem_type] = node
            }}
          >
            <WorklistGroup
              runId={runId}
              rooftopSlug={rooftopSlug}
              slug={entry.problem_type}
              count={entry.count}
              info={registry?.[entry.problem_type] ?? null}
              expanded={expandedSlugs.has(entry.problem_type)}
              onToggle={() => toggleGroup(entry.problem_type)}
              workedMarks={marksBySlug(entry.problem_type)}
              onRowClick={setSelectedVehicle}
            />
          </div>
        ))}
      {searchResults === null && (
        <ResolvedDigest
          resolved={diff?.resolved ?? []}
          previousRunDate={diff?.previous_started_at ?? null}
          registry={registry}
        />
      )}
      {selectedVehicle && dealerGroupId !== null && (
        // Its own boundary, separate from the worklist's: the drawer renders
        // notes, history and vendor detail for one car, and a fault in any of
        // those must not take down the list the operator is working from.
        <ErrorBoundary surface="row-drawer">
          <RowDrawer
            vehicle={selectedVehicle}
            info={
              selectedVehicle.problem_type
                ? registry?.[selectedVehicle.problem_type] ?? null
                : null
            }
            dealerGroupId={dealerGroupId}
            runId={runId}
            labelFor={(slug) => registry?.[slug]?.label ?? slug}
            worked={
              selectedVehicle.vin !== null &&
              selectedVehicle.problem_type !== null &&
              marks.some(
                (m) =>
                  m.vin === selectedVehicle.vin &&
                  m.problem_type === selectedVehicle.problem_type
              )
            }
            onToggleWorked={toggleWorked}
            onClose={() => setSelectedVehicle(null)}
          />
        </ErrorBoundary>
      )}
    </div>
  )
}
